<template>
    <Modal @shown="shown" @hidden="hidden" ref="modal" modalClass="modal-lg" @submit="submit">
        <template #title>
            <template v-if="application && !readonly">
                {{ i18n('Edit {name}').replace('{name}', i18n('Application')) }}
            </template>
            <template v-else-if="application">
                {{ i18n('{name} Detail').replace('{name}', i18n('Application')) }}
            </template>
            <template v-else>
                {{ i18n('Add {name}').replace('{name}', i18n('Application')) }}
            </template>
        </template>

        <div class="alert alert-danger mb-2" v-if="errorMessage">{{ errorMessage }}</div>

        <h2 class="h5">{{ i18n('Main Info') }}</h2>

        <div class="row g-2">
            <div class="col-12" :class="{ 'col-sm-6': $root?.features?.applications?.alias?.enabled }">
                <Field v-model="formData.name" :errorMessage="errorMessages.name" :label="i18n('Name')"
                    :disabled="submitted" :required="true" :type="readonly ? 'readonly' : 'text'" />
            </div>
            <div class="col-12 col-sm-6" v-if="$root?.features?.applications?.alias?.enabled">
                <Field v-model="formData.alias" :errorMessage="errorMessages.alias" :label="i18n('Alias')"
                    :disabled="submitted" :required="true" :type="readonly ? 'readonly' : 'text'" />
            </div>
            <div class="col-12">
                <Field v-model="formData.main_url" :errorMessage="errorMessages.main_url" :label="i18n('Main Url')"
                    :disabled="submitted" :required="true" format="url" :type="readonly ? 'readonly' : 'text'" />
            </div>
            <div class="col-12">
                <Field v-model="formData.description" :errorMessage="errorMessages.description"
                    :label="i18n('Description')" :disabled="submitted" :required="true"
                    :type="readonly ? 'readonly' : 'textarea'" />
            </div>
            <template v-if="$root?.features?.applications?.categories?.enabled">
                <div class="col-12 col-sm-6">
                    <Field v-model="formData.category" :errorMessage="errorMessages.category" :label="i18n('Category')"
                        :disabled="submitted" :type="readonly ? 'readonly' : 'text'" />
                </div>
                <div class="col-12 col-sm-6">
                    <Field v-model="formData.subcategory" :errorMessage="errorMessages.subcategory"
                        :label="i18n('Subcategory')" :disabled="submitted" :type="readonly ? 'readonly' : 'text'" />
                </div>
            </template>
            <div class="col-12" v-if="!$root?.features?.loginPortal?.enabled">
                <Field v-model="formData.svg" :errorMessage="errorMessages.svg" :label="i18n('Svg')"
                    :disabled="submitted" :type="readonly ? 'readonly' : 'textarea'" />
            </div>
        </div>
        <template v-if="$root?.features?.loginPortal?.enabled">
            <h2 class="h5 mt-3">{{ i18n('Portal') }}</h2>
            <div class="row g-2">
                <div class="col-12 col-sm-6">
                    <Field v-model="formData.menu_group" :errorMessage="errorMessages.menu_group"
                        :label="i18n('Menu Group')" :disabled="submitted" :required="true"
                        :type="readonly ? 'readonly' : 'text'" />
                </div>
                <div class="col-12 col-sm-6">
                    <Field v-model="formData.menu_subgroup" :errorMessage="errorMessages.menu_subgroup"
                        :label="i18n('Menu Subgroup')" :disabled="submitted" :required="true"
                        :type="readonly ? 'readonly' : 'text'" />
                </div>
                <div class="col-12">
                    <Field v-model="formData.image_url" :errorMessage="errorMessages.image_url" :label="i18n('Image')"
                        :disabled="submitted" :type="readonly ? 'readonly' : 'imageUrl'" />
                </div>
                <div class="col-12">
                    <Field v-model="formData.is_starred" :errorMessage="errorMessages.is_starred"
                        :label="i18n('Display In Home Page')" :disabled="submitted"
                        :type="readonly ? 'readonly' : 'checkbox'" />
                </div>
                <div class="col-6">
                    <Field v-model="formData.applicationShowLoginType"
                        :errorMessage="errorMessages.applicationShowLoginType" :label="i18n('Show in login')"
                        :disabled="submitted" format="object" :type="readonly ? 'readonly' : 'dictionary'"
                        :list="applicationShowLoginTypes" />
                </div>
                <div class="col-6">
                    <Field v-model="formData.order" :errorMessage="errorMessages.order" :label="i18n('Order in login')"
                        :disabled="submitted" :type="readonly ? 'readonly' : 'text'" />
                </div>
            </div>
        </template>
        <template v-if="!application?.is_self">
            <h2 class="h5 mt-3">{{ i18n('Profiling') }}</h2>
            <div class="row g-2">
                <div class="col-12 col-sm-6">
                    <Field v-model="formData.authorizationType" :errorMessage="errorMessages.authorizationType"
                        :label="i18n('Authorization Type')" :disabled="submitted" :required="true"
                        :type="readonly ? 'readonly' : 'dictionary'" format="object" rest="types/authorizations" />
                </div>
                <div v-if="formData.authorizationType && formData.authorizationType?.code == 'profiles'"
                    class="col-12 col-sm-6">
                    <Field v-model="formData.is_single_profile" :errorMessage="errorMessages.is_single_profile"
                        :label="i18n('Profiling')" :disabled="submitted" :type="readonly ? 'readonly' : 'dictionary'"
                        format="boolean" :hash="{ false: i18n('Multiple profiles'), true: i18n('Unique profile') }" />
                </div>
                <div class="col-6">
                    <Field v-model="formData.is_integrated" :errorMessage="errorMessages.is_identity"
                        :label="i18n('Integrated With %s', appName)" format="boolean" :disabled="submitted"
                        :type="readonly ? 'readonly' : 'dictionary'" :hash="{ false: i18n('No'), true: i18n('Yes') }" />
                </div>
                <template v-if="formData.is_integrated === false">
                    <div class="col-6">
                        <Field v-model="formData.is_identity" :errorMessage="errorMessages.is_identity"
                            :label="i18n('Identity')" format="boolean" :disabled="submitted"
                            :type="readonly ? 'readonly' : 'dictionary'"
                            :hash="{ false: i18n('Delegated identity application'), true: i18n('Self-identity application') }" />
                    </div>
                    <div v-if="formData.is_identity === false" class="col-6">
                        <Field v-model="formData.identityApplication" :errorMessage="errorMessages.identityApplication"
                            :label="i18n('Application that manages identity')" :disabled="submitted"
                            :type="readonly ? 'readonly' : 'dictionary'" format="object" :required="true"
                            rest="applications?is_identity=1" />
                    </div>
                </template>
                <template v-if="!formData.is_self">
                    <div class="col-12">
                        <Field v-model="formData.is_keep_for_internal_disabled_users"
                            :label="i18n('Keep on internal users disabled')" :disabled="submitted"
                            :type="readonly ? 'readonly' : 'checkbox'" />
                    </div>
                    <div class="col-12">
                        <Field v-model="formData.is_exclude_from_duplication"
                            :label="i18n('Exclude when duplicating profiles')" :disabled="submitted"
                            :type="readonly ? 'readonly' : 'checkbox'" />
                    </div>
                    <div class="col-12">
                        <Field v-model="formData.has_otp_seed_access" :label="i18n('Can update/verify OTP seed')"
                            :disabled="submitted" :type="readonly ? 'readonly' : 'checkbox'" />
                    </div>
                </template>
                <div class="col-12" v-if="$root?.features?.applications?.userProfileReplication?.enabled">
                    <Field v-model="formData.is_by_user_profile_replication" :label="i18n('User Profile Replication')"
                        :disabled="submitted" :type="readonly ? 'readonly' : 'checkbox'" />
                </div>
            </div>
        </template>
        <template v-if="$root?.features?.applications?.applicationAuthenticationTypes?.enabled
            && Array.isArray($root.features.applications.applicationAuthenticationTypes.enabled)
            && $root.features.applications.applicationAuthenticationTypes.enabled.length > 1">
            <h2 class="h5 mt-3">{{ i18n('Authentication') }}</h2>
            <div class="row g-2">
                <div class="col-6">
                    <Field v-model="formData.applicationAuthenticationType"
                        :errorMessage="errorMessages.applicationAuthenticationType" :label="i18n('Authentication type')"
                        :disabled="submitted" :type="readonly ? 'readonly' : 'dictionary'" format="object"
                        :required="true" rest="types/applicationAuthentications" />
                </div>
                <div class="col-6"
                    v-if="formData.applicationAuthenticationType && formData.applicationAuthenticationType.code === 'client-credentials'">
                    <Field v-model="formData.ownerExternalUser" :errorMessage="errorMessages.ownerExternalUser"
                        :label="i18n('Application user')" :disabled="submitted"
                        :type="readonly ? 'readonly' : 'dictionary'" format="object" :required="true"
                        rest="users?userTypeCode=app" />
                </div>
            </div>
        </template>

        <h2 class="h5 mt-3">{{ i18n('SAML2 Configuration') }}</h2>
        <div class="row g-2">
            <div class="col-12">
                <Field v-model="formData.is_saml2" :errorMessage="errorMessages.is_saml2" :label="i18n('Enable SAML2')"
                    :disabled="submitted" :type="readonly ? 'readonly' : 'checkbox'" />
            </div>
            <template v-if="formData.is_saml2">
                <div class="col-12">
                    <Field v-model="formData.saml2_entity_id" :errorMessage="errorMessages.saml2_entity_id"
                        :label="i18n('Entity ID')" :disabled="submitted" :required="true"
                        :type="readonly ? 'readonly' : 'text'" />
                </div>
                <div class="col-12">
                    <Field v-model="formData.saml2_acs_url" :errorMessage="errorMessages.saml2_acs_url"
                        :label="i18n('ACS URL')" :disabled="submitted" :required="true"
                        :type="readonly ? 'readonly' : 'text'" />
                </div>
                <div class="col-12" v-if="false">
                    <Field v-model="formData.saml2_sls_url" :errorMessage="errorMessages.saml2_sls_url"
                        :label="i18n('SLS URL')" :disabled="submitted" :required="true"
                        :type="readonly ? 'readonly' : 'text'" />
                </div>
                <div class="col-12">
                    <Field v-model="formData.saml2_certificate" :errorMessage="errorMessages.saml2_certificate"
                        :label="i18n('Certificate')" :disabled="submitted" :type="readonly ? 'readonly' : 'textarea'" />
                </div>
                <div class="col-12">
                    <Field v-model="formData.saml2_name_id_format" :errorMessage="errorMessages.saml2_name_id_format"
                        :label="i18n('Name ID Format')" :disabled="submitted" :required="true"
                        :type="readonly ? 'readonly' : 'text'" />
                </div>
            </template>
        </div>

        <div class="row g-2">
            <div class="col-6">
                <Field v-model="formData.tokenVersion" :errorMessage="errorMessages.tokenVersion"
                    :label="i18n('Token version')" :disabled="submitted" :type="readonly ? 'readonly' : 'dictionary'"
                    format="object" :required="true" rest="types/tokenVersions" />
            </div>
        </div>
        <template #footer>
            <div class="row">
                <template v-if="!readonly">
                    <div class="col text-start">
                        <button type="button" class="btn btn-danger" @click="hide">
                            {{ i18n('Cancel') }}
                        </button>
                    </div>
                    <div class="col text-end">
                        <button type="submit" class="btn btn-primary text-white" :disabled="submitted">
                            {{ i18n('Confirm') }}
                        </button>
                    </div>
                </template>
                <div class="col text-end" v-else>
                    <button type="button" @click="hide" class="btn btn-outline-dark btn-sm me-2">
                        {{ i18n('Close') }}
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script>
import $env from "@/tools/environment.js";
const appName = $env('VITE_APP_NAME');
import api from "@/api.js";
import { duplicate, } from "@/tools/object.js";
import Modal from "@/components/common/layout/Modal.vue";
import Field from "@/components/common/form/Field.vue";
import FieldReadonly from "@/components/common/form/FieldReadonly.vue";
import { translatedErrorMessage, } from "@/tools/errorHandling.js";

export default {
    components: {
        Modal,
        Field,
        FieldReadonly,
    },
    data() {
        return {
            appName,
            applicationId: null,
            application: null,
            submitted: false,
            formData: {},
            errorMessages: {},
            errorMessage: '',
            applicationShowLoginTypes: null,
        };
    },
    emits: [
        'shown',
        'hidden',
        'created',
        'updated',
    ],
    computed: {
        readonly() {
            return this.application
                && this.application.acl?.update !== true;
        },
    },
    methods: {
        show(applicationId) {
            this.applicationId = applicationId;
            this.application = null;
            this.load().then(() => {
                this.submitted = false;
                this.$refs['modal'].show();
                this.errorMessages = {};
                this.errorMessage = '';

                if (this.application) {
                    this.formData = duplicate(this.application);
                } else {
                    this.formData = {
                        is_integrated: true,
                        applicationShowLoginType: this.getDefaultLoginType(),
                    };
                }
            });
        },
        load() {
            const promises = [];

            promises.push(api.cached('types/applicationShowLogin').then(applicationShowLoginTypes => {
                this.applicationShowLoginTypes = applicationShowLoginTypes;
            }));

            if (this.applicationId) {
                promises.push(api.get(`applications/${this.applicationId}`, { acl: 'application' }).then(application => {
                    this.application = application;
                }));
            }

            return Promise.all(promises);
        },
        getDefaultLoginType() {
            const defaultOption = this.applicationShowLoginTypes.find(option => option.code === 'default');
            return defaultOption || null;
        },
        hide() {
            this.$refs['modal'].hide();
        },
        shown() {
            this.$emit('shown');
        },
        hidden() {
            this.$emit('hidden');
        },
        submit() {
            this.submitted = true;
            this.errorMessages = {};
            this.errorMessage = '';

            const postFormData = duplicate(this.formData);
            let savePromise;

            if (!this.application) {
                savePromise = api.post(`applications`, postFormData);
            } else {
                savePromise = api.put(`applications/${this.application.id}`, postFormData);
            }

            savePromise.then(application => {
                if (!this.application) {
                    this.$emit('created', application);
                } else {
                    this.$emit('updated', application);
                }

                this.hide();
                api.clearCache();
            }).catch((errorMessage) => {
                this.errorMessage = translatedErrorMessage(errorMessage);
                this.submitted = false;
            });
        }
    }
}
</script>